import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import './components/ui/ui.css';
import App from './App';
import config from './config/environment';

if (config.sentryDsn) {
  Sentry.init({
    dsn: config.sentryDsn,
    environment: config.environment,
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: config.isProduction ? 0.1 : 1.0,

    // Sentry 11 REMOVED `sendDefaultPii` — it is not deprecated, it is gone
    // (zero occurrences in the shipped types), which is why the upgrade fails
    // to compile with TS2353 until this block replaces it.
    //
    // The replacement is `dataCollection`, and EVERY field in it defaults to
    // `true`. So deleting the old option to make the build pass — the obvious
    // fix — would have started sending exactly the data that option existed to
    // withhold: user identifiers, cookies, headers, query strings, request and
    // response bodies. Each category is therefore turned off explicitly.
    //
    // This keeps the frontend aligned with the backend, which sets
    // `send_default_pii=False` plus an EventScrubber denylist in
    // config/settings/production.py.
    dataCollection: {
      userInfo: false,
      cookies: false,
      httpHeaders: false,
      httpBodies: [],
      urlQueryParams: false,
      // Local variable values from stack frames. Off because a frame caught
      // mid-sign-in can hold a password or token in a local.
      stackFrameVariables: false,
      // Inert today: these only apply when the matching integration is
      // enabled, and none of them are. Set anyway, so that adding one later
      // cannot quietly start collecting — the default would be `true`.
      graphQL: { document: false, variables: false },
      genAI: { inputs: false, outputs: false },
      databaseQueryData: false,
      queues: false,
    },

    // `frameContextLines` is deliberately left at its default of 5: those are
    // lines of our own source, not user data, and they are what makes a
    // minified stack trace readable.

    // Span batching changed in 11: the new default, 'stream', sends spans in
    // interval- and action-triggered batches instead of waiting for the local
    // span tree to complete. That means spans from traces that never complete
    // — a user navigating away mid-interaction — are now sent, where 'static'
    // would have dropped them. Pinned to the old behaviour so this upgrade is
    // a dependency change and not a quota change at the same time; revisit
    // 'stream' deliberately, with the Sentry usage graph open.
    traceLifecycle: 'static',
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Failed to find the root element');
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);

